// Seton Hall University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Seton Hall posts no current Common Data Set (Institutional Research's
//    Reports page links CDS files only through 2004-2005). The first-year counts come from the
//    U.S. Department of Education's IPEDS Admissions survey for Fall 2024,
//    first-time degree-seeking students (College Navigator and the ADM2024
//    complete data file, unit 186584). Quirk: the counselor handbook's "2026
//    Freshman Admitted Student Profile" says 28,070 applied and a 67.40%
//    acceptance rate, without counts admitted; it is not used here.
//  - Seton Hall Undergraduate Admissions: Counselor Handbook ("Seton Hall
//    University 2026/2027 Guide to Admissions": dates and deadlines),
//    Frequently Asked Questions (early action, decision timing, Honors
//    Program), and Test-Optional Admission (test-optional through the Fall
//    2029 admissions cycle). Decision dates disagree: the 2026/2027 Guide
//    says Early Action I decisions are released in mid-December and Early
//    Action II in mid-January, while the FAQ says Early Action I applicants
//    whose materials are in by November 15 hear by December 30. Both are
//    stated. The Freshman Applicants checklist page repeats the four
//    deadlines but still names "Fall 2025 applicants" for the FAFSA, so it is
//    not cited.
//  - Seton Hall Office of the Bursar, Tuition and Fees, Academic Year 2027
//    Rates (Fall 2026 - Summer 2027): flat full-time tuition, university and
//    mobile computing fees, the one-time new student fee, the on-campus
//    double room and the U meal plan, each per semester times two. Seton
//    Hall posts no full cost of attendance; the bursar lists billed charges
//    only. The 2026-2027 Undergraduate Catalogue's "Estimates of Non-Tuition
//    Costs" (on campus: books and supplies $1,000, transportation $1,500,
//    miscellaneous $1,500) are named in the note, not added to the total.
//  - College Scorecard, school id 186584 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=186584#admsns',
        read: '2026-10-01',
    },
    handbook: {
        label: 'Seton Hall Undergraduate Admissions, Counselor Handbook: 2026/2027 Guide to Admissions',
        url: 'https://www.shu.edu/undergraduate-admissions/counselor-handbook.html',
        read: '2026-10-01',
    },
    faq: {
        label: 'Seton Hall Undergraduate Admissions, Frequently Asked Questions',
        url: 'https://www.shu.edu/undergraduate-admissions/frequently-asked-questions.html',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'Seton Hall Undergraduate Admissions, Test-Optional Admission',
        url: 'https://www.shu.edu/undergraduate-admissions/test-optional-admission.html',
        read: '2026-10-01',
    },
    bursar: {
        label: 'Seton Hall Office of the Bursar, Tuition and Fees, Academic Year 2027 Rates',
        url: 'https://www.shu.edu/bursar/tuition-and-fees.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?186584-Seton-Hall-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'seton-hall-university',
    kbId: 'seton_hall_university',
    name: 'Seton Hall University',
    shortName: 'Seton Hall',
    location: 'South Orange, NJ',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Seton Hall visual identity page); decoration only.
    branding: {
        primary: '#0060A9',
        secondary: '#003263',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/seton_hall_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 24776,
        admitted: 18152,
        enrolled: 1601,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates (Seton Hall posts no current Common Data Set)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'IPEDS reports one first-year acceptance rate for the whole university, and Seton Hall publishes no rate by college or major.',
        source: 'ipeds',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Seton Hall as a freshman. One program takes its own application:',
        rows: [
            { school: 'University Honors Program', requirement: 'A separate application. Admission goes to students with exemplary grades, test scores and leadership in high school; generally a minimum 3.5 GPA and a 27 on the ACT or a 660 in SAT reading and writing are required.' },
        ],
        source: 'faq',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision I', value: 'January 15' },
        rows: [
            { plan: 'Early Action I', deadline: 'November 15', decision: 'Mid-December', binding: false },
            { plan: 'Early Action II', deadline: 'December 15', decision: 'Mid-January', binding: false },
            { plan: 'Regular Decision I', deadline: 'January 15', decision: null, binding: false },
            { plan: 'Regular Decision II', deadline: 'February 15', decision: null, binding: false },
        ],
        note: 'The application opens August 1 and the fee is $65. The scholarship application deadline is January 15 and the enrollment deadline is May 1. The decision dates are the 2026/2027 Guide\'s; Seton Hall\'s FAQ instead says Early Action I applicants whose materials are all in by November 15 hear by December 30, and that a decision usually takes 4 to 6 weeks once an application is complete. Seton Hall publishes no decision date for Regular Decision.',
        source: 'handbook',
        binding: 'Early action is not binding. It tells Seton Hall it is among your top choices, and your application is among the first reviewed for admission and scholarships. Seton Hall has no early decision plan.',
        bindingSource: 'faq',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Seton Hall does not require SAT or ACT scores through the Fall 2029 admissions cycle. You choose on the Seton Hall application or the Common Application whether to apply test-optional, and test-optional applicants get the same consideration. Seton Hall strongly encourages you to send scores of 1200 SAT (25 ACT) or higher. Scores may be required for specific academic programs and some scholarships.',
        source: 'testOptional',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testOptional',
    },

    cost: {
        year: '2026-2027',
        partial: true,
        rows: [
            { item: 'Tuition (full-time flat rate)', amount: 53840 },
            { item: 'University fee', amount: 2290 },
            { item: 'Mobile computing fee', amount: 670 },
            { item: 'New student fee (one time)', amount: 380 },
            { item: 'Housing (on-campus double room)', amount: 13100 },
            { item: 'Food (U meal plan)', amount: 6100 },
        ],
        total: 76380,
        note: 'Billed charges for a first-year student taking 12 to 18 semester hours, in a standard on-campus double room on the U meal plan, fall 2026 and spring 2027. The other unlimited meal plans cost more. Seton Hall posts no full cost of attendance: books, transportation and personal costs are extra, and its 2026-2027 Undergraduate Catalogue estimates them at $1,000, $1,500 and $1,500 for students on campus.',
        source: 'bursar',
        where: 'Academic Year 2027 Rates (Fall 2026 - Summer 2027)',
        netPrice: {
            average: 31552,
            byIncome: [
                { income: '$0 to $30,000', amount: 22553 },
                { income: '$30,001 to $48,000', amount: 21173 },
                { income: '$48,001 to $75,000', amount: 24213 },
                { income: '$75,001 to $110,000', amount: 34049 },
                { income: 'Over $110,000', amount: 41574 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Seton Hall\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
